import { ChannelsDTO, ChannelsResponse, WebChatAdditionalData } from "../../../models/channel"
import { ChannelDeclarations, ChannelDeclarationsDto } from "../../../models/parameterDeclaration"
import {
    FETCH_CHANNELS_DECLARATIONS_FAILED_MESSAGE,
    FETCH_CHANNELS_FAILED_MESSAGE,
    GET_WEBCHAT_CHANNEL_ADDITIONAL_DATA_FAILED_MESSAGE
} from "./const"
import { mapChannelDtoToChannel, mapToChannelDeclarations } from "../../../utility/channels/channelTranslator"
import { queryApi } from "../../api"
import { handleQueryApiError } from "../../utility/error"
import { getChannelsTag, getWebChatAdditionalDataTag } from "../../tags"

export const channelsApi = queryApi.injectEndpoints({
    endpoints(build) {
        return {
            getDeclarations: build.query<ChannelDeclarations, void>({
                query: () => ({
                    url: "/channels/declarations"
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled
                    } catch (e) {
                        handleQueryApiError(dispatch, FETCH_CHANNELS_DECLARATIONS_FAILED_MESSAGE, e)
                    }
                },
                transformResponse: (response: ChannelDeclarationsDto) => mapToChannelDeclarations(response)
            }),
            getChannels: build.query<ChannelsResponse, { projectId: string; declarations: ChannelDeclarations }>({
                query: ({ projectId }) => ({
                    url: `/${projectId}/channels`
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled
                    } catch (e) {
                        handleQueryApiError(dispatch, FETCH_CHANNELS_FAILED_MESSAGE, e)
                    }
                },
                serializeQueryArgs: ({ queryArgs: { projectId } }) => projectId,
                transformResponse: (response: ChannelsDTO, _, { declarations }) => {
                    const { channels, webChatAdditionalData } = response

                    return {
                        channels: channels.map(channel => mapChannelDtoToChannel(channel, declarations)),
                        webChatAdditionalData: webChatAdditionalData ?? {}
                    }
                },
                providesTags: (_, __, { projectId }) => [{ type: getChannelsTag, projectId }]
            }),
            getWebChatAdditionalData: build.query<WebChatAdditionalData, { projectId: string; channelId: string }>({
                query: ({ projectId, channelId }) => ({
                    url: `/${projectId}/channels/webchat/${channelId}/additional-data`
                }),
                async onQueryStarted({ projectId, channelId }, { dispatch, queryFulfilled, getState }) {
                    try {
                        const { data } = await queryFulfilled
                        const state = getState()
                        const channels = channelsApi.endpoints.getChannels.select({ projectId, declarations: {} })(
                            state
                        )

                        if (channels?.data) {
                            dispatch(
                                channelsApi.util.updateQueryData(
                                    "getChannels",
                                    { projectId, declarations: {} },
                                    draft => {
                                        if (!draft.webChatAdditionalData) {
                                            draft.webChatAdditionalData = {}
                                        }
                                        draft.webChatAdditionalData[channelId] = data
                                    }
                                )
                            )
                        }
                    } catch (e) {
                        handleQueryApiError(dispatch, GET_WEBCHAT_CHANNEL_ADDITIONAL_DATA_FAILED_MESSAGE, e)
                    }
                },
                providesTags: (_, __, { projectId }) => [{ type: getWebChatAdditionalDataTag, projectId }]
            })
        }
    }
})

export const {
    useGetChannelsQuery,
    useLazyGetChannelsQuery,
    useGetDeclarationsQuery,
    useGetWebChatAdditionalDataQuery
} = channelsApi
